import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Alert } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { downloadAismSdkLauncher, downloadAismUserGuide, getAiSMReleaseNote, getLatestVersionInfo, getReleasedVersionInfo } from '../../../../services/sealing/ios';
import { RootState } from '../../../../store';
import LoadMask from '../../../utils/loadmask';
import { errorDialog, showToast } from '../../../utils/notifications';
import UpgradeYourPlan from '../../../utils/upgradePlan';
import Arm64Popup from './arm64Popup';
import './index.less';
import X64Popup from './x64Popup';

const Aism = () => {
    const { i18n, t } = useTranslation();
    const [latestVersion, setLatestVersion] = useState<string | null>(null);
    const [releasedVersion, setReleasedVersion] = useState<string | null>(null);
    const [selectedKey, setSelectedKey] = useState<string>('');
    const [isPlanValid, setIsPlanValid] = useState<boolean | null>(null);
    const [isHybridPlanValid, setIsHybridPlanValid] = useState<boolean | null>(null);
    const [releaseNote, setReleaseNote] = useState<string | null>(null);
    const region = useSelector((state: RootState) => state.mainReducer.region);
    const authContext = useSelector((state: RootState) => state.mainReducer.authContext);
    const planDetails = useSelector((state: RootState) => state.mainReducer.planDetails);
    const [showMask, setShowMask] = useState<any>({ msg: t('verifying_plan_details_for_ios') });

    useEffect(() => {
        if (planDetails) {
            setShowMask(null);
            if (planDetails.plan?.ios?.type === 'E' || planDetails.plan?.ios?.type === 'P' || planDetails.plan?.hybrid?.type === 'E' || planDetails.plan?.hybrid?.type === 'P') {
                if (planDetails.plan?.ios?.type === 'E' || planDetails.plan?.ios?.type === 'P') setIsPlanValid(true);
                else setIsPlanValid(false);
                if (planDetails.plan?.hybrid?.type === 'E' || planDetails.plan?.hybrid?.type === 'P') setIsHybridPlanValid(true);
                else setIsHybridPlanValid(false);
            } else {
                setIsPlanValid(false);
                setIsHybridPlanValid(false);
            }
        }
    }, [planDetails]);
    useEffect(() => {
        getVersionInfo();
        getReleasedInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getReleaseNote = async (version: string) => {
        const response = await getAiSMReleaseNote(version || '');
        if (i18n.language === 'kr_KR') {
            setReleaseNote(response['release-note'].deploy_content_kr);
        } else {
            setReleaseNote(response['release-note'].deploy_content_en);
        }
    };

    const onDownloadSdk = async (type: string) => {
        let config =
            type === 'latest'
                ? {
                      type: 'arm64',
                      region,
                  }
                : {
                      type: 'x64',
                      region,
                  };

        const response = await downloadAismSdkLauncher(config, region);
        if (response.result.code === '0000') {
            try {
                const newWindow = window.open(response.sdk_url, '_blank');
                if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                    onPopupError(response.sdk_url);
                }
            } catch (err) {
                onPopupError(response.sdk_url);
            }
        } else {
            showToast('error', t(`RCM-${response.result.code}`));
        }
    };

    const onPopupError = (urlDownload: string | undefined) => {
        errorDialog({
            title: t('popup_error_title'),
            content: (
                <div>
                    <span>{t('popup_error_message')}</span>
                    <a className="text-linkblue hover:underline" href={urlDownload} download>
                        {t('popup_error_message2')}
                    </a>
                    .
                </div>
            ),
            okText: t('ok'),
        });
    };

    const getVersionInfo = async () => {
        const response = await getLatestVersionInfo(authContext.token, region);
        if (response.latest_version_info) {
            setLatestVersion(response.latest_version_info.service_version);
            getReleaseNote(response.latest_version_info.service_version);
        }
    };

    const getReleasedInfo = async () => {
        const response = await getReleasedVersionInfo(authContext.token, region);
        if (response.release_versions) {
            const highestVersion = response.release_versions.reduce((max: { seq: number }, current: { seq: number }) => (current.seq > max.seq ? current : max), response.release_versions[0]);
            setReleasedVersion(highestVersion.service_version);
        }
    };

    const onChange = (key: string | string[]) => {
        setSelectedKey(key as string);
    };

    const downloadGuide = async () => {
        const config = {
            version: latestVersion || '',
            lang: i18n.language === 'kr_KR' ? 'KR' : 'EN',
        };
        const response = await downloadAismUserGuide(config);
        if (response.result.code === '0000') {
            try {
                const newWindow = window.open(response.sdk_url, '_blank');
                if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                    onPopupError(response.sdk_url);
                }
            } catch (err) {
                onPopupError(response.sdk_url);
            }
        } else {
            showToast('error', t(`RCM-${response.result.code}`));
        }
    };

    return (
        <>
            {showMask && <LoadMask msg={showMask.msg} />}
            {isPlanValid === false && isHybridPlanValid === false && <UpgradeYourPlan />}
            {typeof isPlanValid === 'boolean' && typeof isHybridPlanValid === 'boolean' && (isPlanValid === true || isHybridPlanValid === true) && (
                <Box className="grid content-start gap-6">
                    <Box className="text-2xl font-medium">{t('aism_title')}</Box>
                    <Box className="max-w-[800px] text-sm">
                        <Box>
                            {t('aism_desc')}
                            <span onClick={downloadGuide} className="ml-1 underline cursor-pointer text-linkblue">
                                {t('aism_desc_link')}
                            </span>
                        </Box>
                        <Box className="mt-6">
                            <Box className="font-bold">{t('aism_scope_title')}</Box>
                            <Box>{t('aism_scope_desc')}</Box>
                        </Box>
                    </Box>

                    <Box className="flex gap-4 mt-4">
                        <Arm64Popup disabled={!latestVersion} onClick={onDownloadSdk} btnText={`Arm64 ${t('version')} ${latestVersion}`} />
                        <X64Popup disabled={!releasedVersion} onClick={onDownloadSdk} btnText={`x64 ${t('version')} ${releasedVersion}`} />
                    </Box>

                    <Box className="mt-10">
                        <Alert severity="error" className="mb-10">{t('aism_alert')}</Alert>
                        <Box className="max-w-[800px]">
                            <Accordion expanded={selectedKey === '1'} onChange={(event, isExpanded) => onChange(isExpanded ? '1' : '')} sx={{ boxShadow: 'none', '&::before': { display: 'none' } }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} className="p-0">
                                    <Box className="flex items-center w-full">
                                        <p className="text-xl font-semibold min-w-[125px]">{t('release_note')}</p>
                                        <p className="mx-2 w-full h-px bg-[#E0E0E0]" />
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box className="flex gap-8">
                                        <Box className="text-sm font-semibold">{latestVersion || ''}</Box>
                                        <Box className="whitespace-pre-wrap">{releaseNote}</Box>
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        </Box>
                    </Box>
                </Box>
            )}
        </>
    );
};

export default Aism;
